import { useState, useEffect, useCallback, useRef } from "react";
import { api, formatApiError, fileUrl } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { motion } from "framer-motion";
import {
  X, Clock, Users, Crown, Play, Stop, FloppyDisk, Microphone, Paperclip, Sparkle,
  CheckCircle, Prohibit, BellRinging, GearSix, Trash, FileText, ListChecks,
} from "@phosphor-icons/react";

const inputCls = "w-full h-11 px-3 rounded-lg border-2 border-black bg-white outline-none focus:ring-2 focus:ring-primary text-sm";
const labelCls = "text-xs font-bold uppercase tracking-[0.12em] text-black/60 mb-2 block";

function Toggle({ checked, onChange, disabled, testid }) {
  return (
    <button type="button" role="switch" aria-checked={checked} data-testid={testid} disabled={disabled}
      onClick={() => !disabled && onChange(!checked)}
      className={`relative w-12 h-7 rounded-full border-2 border-black transition-colors shrink-0 ${checked ? "bg-primary" : "bg-white"} ${disabled ? "opacity-40 cursor-not-allowed" : "press"}`}>
      <span className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full border-2 border-black bg-white transition-transform ${checked ? "translate-x-5" : "translate-x-0"}`} />
    </button>
  );
}

export default function MeetingDetail({ meetingId, autoRecord, onClose, onChanged, onOpenSettings, onGenerate }) {
  const { user } = useAuth();
  const [m, setM] = useState(null);
  const [notes, setNotes] = useState("");
  const [savingNotes, setSavingNotes] = useState(false);
  const [showEnd, setShowEnd] = useState(false);
  const [recording, setRecording] = useState(false);
  const [seconds, setSeconds] = useState(0);
  const mediaRef = useRef(null);
  const timerRef = useRef(null);
  const fileRef = useRef(null);
  const startRecRef = useRef(null);
  const autoStartedRef = useRef(false);
  const sessionRef = useRef(null);
  const idxRef = useRef(0);
  const uploadsRef = useRef([]);
  const wakeLockRef = useRef(null);
  const streamRef = useRef(null);
  const mimeRef = useRef("audio/webm");
  const recordingRef = useRef(false);
  const intentionalStopRef = useRef(false);
  const resumingRef = useRef(false);
  const secondsRef = useRef(0);
  const [finalizing, setFinalizing] = useState(false);
  const [pendingRec, setPendingRec] = useState(null);

  const load = useCallback(async () => {
    const { data } = await api.get(`/meetings/${meetingId}`);
    setM(data); setNotes(data.notes || "");
  }, [meetingId]);

  useEffect(() => { load(); }, [load]);
  useEffect(() => () => clearInterval(timerRef.current), []);

  useEffect(() => {
    if (autoRecord && m?.status === "in_progress" && m?.can_edit && !recording && !autoStartedRef.current) {
      autoStartedRef.current = true;
      startRecRef.current?.();
    }
  }, [autoRecord, m, recording]);

  const requestWakeLock = async () => {
    try { if ("wakeLock" in navigator) wakeLockRef.current = await navigator.wakeLock.request("screen"); } catch {}
  };
  const releaseWakeLock = () => { try { wakeLockRef.current?.release?.(); } catch {} wakeLockRef.current = null; };

  const checkPending = useCallback(async () => {
    try { const { data } = await api.get(`/meetings/${meetingId}/recording/pending`); setPendingRec(data?.pending ? data : null); }
    catch { setPendingRec(null); }
  }, [meetingId]);

  useEffect(() => { if (m && !recording && !m.recording) checkPending(); }, [m, recording, checkPending]);

  useEffect(() => {
    if (!recording) return undefined;
    const beforeUnload = (e) => { e.preventDefault(); e.returnValue = ""; };
    const onVis = () => {
      if (document.visibilityState === "visible") {
        requestWakeLock();
        // Capture may have been suspended while backgrounded / asleep — pick it back up.
        if (recordingRef.current && !intentionalStopRef.current && (!mediaRef.current || mediaRef.current.state !== "recording")) resumeRec();
      } else {
        // Backgrounded — flush whatever is buffered right now so nothing is lost.
        try { mediaRef.current?.requestData?.(); } catch {}
      }
    };
    window.addEventListener("beforeunload", beforeUnload);
    document.addEventListener("visibilitychange", onVis);
    return () => { window.removeEventListener("beforeunload", beforeUnload); document.removeEventListener("visibilitychange", onVis); };
  }, [recording]); // eslint-disable-line

  if (!m) return null;
  const canEdit = m.can_edit;
  const aiOn = m.ai_summary_enabled;

  const saveNotes = async () => {
    setSavingNotes(true);
    try { await api.put(`/meetings/${meetingId}/notes`, { notes }); toast.success("Notes saved"); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } finally { setSavingNotes(false); }
  };

  const setAi = async (enabled) => {
    try { const { data } = await api.patch(`/meetings/${meetingId}/ai-toggle`, { enabled }); setM({ ...data, can_edit: canEdit }); onChanged?.(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  const start = async () => { try { await api.post(`/meetings/${meetingId}/start`); await load(); onChanged?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };
  const end = async () => { try { if (recording) stopRec(); await api.post(`/meetings/${meetingId}/end`); await load(); onChanged?.(); setShowEnd(true); } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); } };

  const finalizeRecording = async () => {
    releaseWakeLock();
    setFinalizing(true);
    try {
      await Promise.allSettled(uploadsRef.current);
      const fd = new FormData();
      fd.append("session_id", sessionRef.current);
      fd.append("duration_seconds", String(secondsRef.current));
      fd.append("content_type", mimeRef.current || "audio/webm");
      await api.post(`/meetings/${meetingId}/recording/finalize`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recording saved");
      setPendingRec(null); load();
    } catch (e) { toast.error("Couldn't save the recording. You can recover it from this meeting."); checkPending(); }
    finally { setFinalizing(false); }
  };

  // Attach a MediaRecorder to a stream. Reused for the initial start AND for auto-resume
  // after an interruption, always keeping the SAME session_id + segment index so the audio
  // stitches back together seamlessly on the server.
  const attachRecorder = (stream) => {
    const mr = new MediaRecorder(stream);
    mimeRef.current = mr.mimeType || "audio/webm";
    mr.ondataavailable = (e) => {
      if (!e.data || e.data.size === 0) return;
      const i = idxRef.current++;
      const fd = new FormData();
      fd.append("session_id", sessionRef.current);
      fd.append("idx", String(i));
      fd.append("file", new File([e.data], `seg${i}.webm`, { type: mimeRef.current }));
      const p = api.post(`/meetings/${meetingId}/recording/segment`, fd, { headers: { "Content-Type": "multipart/form-data" } })
        .catch((err) => { console.error("segment upload failed", err); });
      uploadsRef.current.push(p);
    };
    mr.onstop = () => {
      try { stream.getTracks().forEach((t) => t.stop()); } catch {}
      if (intentionalStopRef.current) finalizeRecording();
      // Otherwise this was an interruption (screen lock / sleep / mic lost). Everything
      // captured so far is already uploaded and safe; we auto-resume when the app is
      // visible again (see the visibility handler + track.onended below).
    };
    mr.onerror = () => { if (recordingRef.current && !intentionalStopRef.current && document.visibilityState === "visible") resumeRec(); };
    stream.getAudioTracks().forEach((t) => {
      t.onended = () => { if (recordingRef.current && !intentionalStopRef.current && document.visibilityState === "visible") resumeRec(); };
    });
    mr.start(5000); // flush a segment every 5s, so at most a few seconds are ever at risk
    mediaRef.current = mr; streamRef.current = stream;
  };

  const startRec = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      sessionRef.current = `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
      idxRef.current = 0; uploadsRef.current = []; intentionalStopRef.current = false;
      attachRecorder(stream);
      recordingRef.current = true; setRecording(true); setSeconds(0); secondsRef.current = 0;
      timerRef.current = setInterval(() => setSeconds((s) => { secondsRef.current = s + 1; return s + 1; }), 1000);
      requestWakeLock();
    } catch { toast.error("Microphone permission denied."); }
  };
  startRecRef.current = startRec;

  // Re-acquire the mic and keep recording into the same session after an interruption.
  const resumeRec = async () => {
    if (!recordingRef.current || intentionalStopRef.current || resumingRef.current) return;
    if (mediaRef.current && mediaRef.current.state === "recording") return;
    resumingRef.current = true;
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      if (!recordingRef.current || intentionalStopRef.current) { stream.getTracks().forEach((t) => t.stop()); return; }
      attachRecorder(stream);
      await requestWakeLock();
      toast.message("Recording resumed");
    } catch (e) { /* mic not ready yet — will retry when the app is foregrounded again */ }
    finally { resumingRef.current = false; }
  };

  const stopRec = () => {
    intentionalStopRef.current = true;
    recordingRef.current = false;
    clearInterval(timerRef.current);
    try { mediaRef.current?.requestData?.(); } catch {}
    try { mediaRef.current?.stop(); } catch {}
    setRecording(false);
  };

  const recoverRec = async () => {
    if (!pendingRec) return;
    setFinalizing(true);
    try {
      const fd = new FormData();
      fd.append("session_id", pendingRec.session_id);
      fd.append("duration_seconds", "0");
      fd.append("content_type", "audio/webm");
      await api.post(`/meetings/${meetingId}/recording/finalize`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Recovered your recording"); setPendingRec(null); load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setFinalizing(false); }
  };

  const onFile = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    const fd = new FormData(); fd.append("file", file); fd.append("use_for_ai", "true");
    try { await api.post(`/meetings/${meetingId}/attachments`, fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success("Attachment added"); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail)); }
    e.target.value = "";
  };
  const toggleAttAi = async (att) => { try { await api.patch(`/meetings/${meetingId}/attachments/${att.id}`, { enabled: !att.use_for_ai }); load(); } catch {} };
  const delAtt = async (att) => { try { await api.delete(`/meetings/${meetingId}/attachments/${att.id}`); load(); } catch {} };

  const statusBadge = { scheduled: "#E0F2FE", in_progress: "#DCFCE7", ended: "#F3F4F6", cancelled: "#FEE2E2" }[m.status];

  return (
    <div className="fixed inset-0 z-[80] flex justify-center items-start overflow-y-auto p-4 bg-black/50" onClick={onClose}>
      <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }}
        data-testid="meeting-detail" onClick={(e) => e.stopPropagation()}
        className="relative bg-background border-2 border-black rounded-2xl hard-shadow w-full max-w-2xl my-6">
        <div className="sticky top-0 bg-white border-b-2 border-black px-5 py-4 flex items-center justify-between z-10 rounded-t-2xl">
          <div className="flex items-center gap-2 min-w-0">
            <span className="text-[10px] font-bold px-2 py-0.5 rounded-full border-2 border-black capitalize" style={{ backgroundColor: statusBadge }}>{m.status.replace("_", " ")}</span>
            <h2 className="font-display text-lg font-bold truncate">{m.title}</h2>
          </div>
          <button data-testid="meeting-detail-close" onClick={onClose} className="press w-9 h-9 rounded-full border-2 border-black flex items-center justify-center bg-white shrink-0"><X size={16} weight="bold" /></button>
        </div>

        <div className="p-5 space-y-5">
          {m.description && <p className="text-sm text-black/70">{m.description}</p>}
          <div className="flex flex-wrap gap-3 text-xs text-black/60">
            <span className="flex items-center gap-1"><Clock size={14} /> {m.scheduled_at ? new Date(m.scheduled_at).toLocaleString(undefined, { timeZone: "Pacific/Honolulu" }) : "No date"} · {m.duration_minutes}m</span>
            <span className="flex items-center gap-1"><Crown size={14} weight="fill" /> {m.organizer_name}</span>
            <span className="flex items-center gap-1"><Users size={14} /> {m.attendees?.map((a) => a.name).join(", ") || "No attendees"}</span>
          </div>

          {/* Meeting-level AI toggle */}
          {canEdit && (
            <div className="flex items-center justify-between gap-3 p-4 rounded-xl border-2 border-black bg-[#F3E8FF]">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-lg border-2 border-black flex items-center justify-center bg-white"><Sparkle size={18} weight="fill" /></div>
                <div>
                  <p className="font-bold text-sm">Use AI Summary for this meeting</p>
                  <p className="text-xs text-black/50">Overrides your default only for this meeting.</p>
                </div>
              </div>
              <Toggle testid="meeting-ai-toggle" checked={aiOn} onChange={setAi} />
            </div>
          )}

          {/* Lifecycle */}
          {canEdit && (
            <div className="flex gap-3">
              {m.status === "scheduled" && <button data-testid="meeting-start-btn" onClick={start} className="press flex-1 h-11 rounded-full bg-secondary text-white font-bold border-2 border-black flex items-center justify-center gap-2"><Play size={16} weight="fill" /> Start meeting</button>}
              {m.status === "in_progress" && <button data-testid="meeting-end-btn" onClick={end} className="press flex-1 h-11 rounded-full bg-primary text-white font-bold border-2 border-black flex items-center justify-center gap-2"><Stop size={16} weight="fill" /> End meeting</button>}
              {m.status === "ended" && <button data-testid="meeting-endscreen-btn" onClick={() => setShowEnd(true)} className="press flex-1 h-11 rounded-full bg-white font-bold border-2 border-black flex items-center justify-center gap-2"><ListChecks size={16} weight="bold" /> Post-meeting actions</button>}
            </div>
          )}

          {/* Notes */}
          <div>
            <label className={labelCls}>Meeting notes</label>
            <textarea data-testid="meeting-notes-input" disabled={!canEdit} value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Type meeting notes here…" className={inputCls + " h-40 py-2 resize-none disabled:opacity-70"} />
            {canEdit && <button data-testid="meeting-notes-save" onClick={saveNotes} disabled={savingNotes} className="press mt-2 h-9 px-4 rounded-full bg-white font-bold text-sm border-2 border-black flex items-center gap-2"><FloppyDisk size={14} weight="bold" /> {savingNotes ? "Saving…" : "Save notes"}</button>}
          </div>

          {/* Recording */}
          {canEdit && (
            <div>
              <label className={labelCls}>Voice recording</label>
              <div className="flex items-center gap-3 flex-wrap">
                {!recording ? (
                  <button data-testid="meeting-record-btn" onClick={startRec} disabled={finalizing} className="press h-10 px-4 rounded-full bg-white font-bold text-sm border-2 border-black flex items-center gap-2 disabled:opacity-50"><Microphone size={16} weight="fill" /> Record</button>
                ) : (
                  <button data-testid="meeting-stop-rec-btn" onClick={stopRec} className="press h-10 px-4 rounded-full bg-primary text-white font-bold text-sm border-2 border-black flex items-center gap-2 animate-pulse"><Stop size={16} weight="fill" /> Stop · {Math.floor(seconds / 60)}:{String(seconds % 60).padStart(2, "0")}</button>
                )}
                {finalizing && <span className="text-xs font-semibold text-black/60">Saving recording…</span>}
                {m.recording && <a href={fileUrl(m.recording.storage_path)} target="_blank" rel="noreferrer" className="text-sm font-bold underline">Play recording</a>}
              </div>
              {recording && <p className="text-[11px] text-black/50 mt-2">Keep this screen open while recording — your audio is saved every few seconds. If your phone locks or sleeps, recording pauses and automatically resumes when you return, with nothing lost.</p>}
              {!recording && pendingRec && (
                <div data-testid="meeting-recover-rec" className="mt-3 p-3 rounded-lg border-2 border-black bg-[#FEF9C3] flex items-center justify-between gap-2">
                  <p className="text-xs font-semibold text-black/70">We found an unsaved recording from an interrupted session.</p>
                  <button data-testid="meeting-recover-btn" onClick={recoverRec} disabled={finalizing} className="press h-9 px-3 rounded-full bg-black text-white font-bold text-xs shrink-0 disabled:opacity-50">{finalizing ? "Recovering…" : "Recover it"}</button>
                </div>
              )}
            </div>
          )}

          {/* Attachments */}
          <div>
            <label className={labelCls}>Attachments</label>
            {canEdit && (
              <>
                <input ref={fileRef} type="file" className="hidden" onChange={onFile} data-testid="meeting-attach-input" />
                <button data-testid="meeting-attach-btn" onClick={() => fileRef.current?.click()} className="press h-10 px-4 rounded-full bg-white font-bold text-sm border-2 border-black flex items-center gap-2 mb-2"><Paperclip size={16} weight="bold" /> Add file</button>
              </>
            )}
            <div className="space-y-2">
              {(m.attachments || []).map((a) => (
                <div key={a.id} data-testid={`meeting-att-${a.id}`} className="flex items-center justify-between gap-2 p-2.5 rounded-lg border-2 border-black bg-white text-sm">
                  <span className="flex items-center gap-2 min-w-0"><FileText size={16} /> <span className="truncate">{a.filename}</span></span>
                  <div className="flex items-center gap-2 shrink-0">
                    <label className="text-[10px] font-bold flex items-center gap-1"><Sparkle size={10} weight="fill" /> AI</label>
                    <Toggle testid={`meeting-att-ai-${a.id}`} checked={a.use_for_ai} onChange={() => toggleAttAi(a)} disabled={!canEdit} />
                    {canEdit && <button onClick={() => delAtt(a)} className="press w-7 h-7 rounded-full border-2 border-black flex items-center justify-center bg-white"><Trash size={12} weight="bold" /></button>}
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* End-meeting screen */}
        {showEnd && (
          <div className="absolute inset-0 z-20 bg-background/98 rounded-2xl overflow-y-auto" data-testid="meeting-end-screen">
            <div className="p-6 space-y-4">
              <div className="text-center">
                <div className="w-12 h-12 rounded-xl bg-secondary border-2 border-black flex items-center justify-center mx-auto mb-2"><CheckCircle size={26} weight="fill" color="#fff" /></div>
                <h3 className="font-display text-2xl font-black tracking-tighter">Meeting ended</h3>
                <p className="text-sm text-black/60">Choose what to do next.</p>
              </div>

              <button data-testid="endscreen-complete-notes" onClick={() => setShowEnd(false)} className="press w-full h-12 rounded-xl border-2 border-black bg-white font-bold flex items-center gap-3 px-4"><FileText size={20} weight="bold" /> Complete meeting notes</button>
              <button data-testid="endscreen-review-actions" onClick={() => setShowEnd(false)} className="press w-full h-12 rounded-xl border-2 border-black bg-white font-bold flex items-center gap-3 px-4"><ListChecks size={20} weight="bold" /> Review action items</button>

              {aiOn ? (
                <button data-testid="endscreen-generate-ai" onClick={() => { setShowEnd(false); onGenerate?.(m); }} className="press w-full h-12 rounded-xl border-2 border-black bg-primary text-white font-bold flex items-center gap-3 px-4"><Sparkle size={20} weight="fill" /> Generate AI Summary</button>
              ) : (
                <div className="rounded-xl border-2 border-black bg-[#FEF9C3] p-4">
                  <p className="font-bold text-sm flex items-center gap-2"><Prohibit size={18} weight="bold" /> AI Summary Disabled</p>
                  <p className="text-xs text-black/60 mt-1">“AI Meeting Summary is turned off. You can still create and edit meeting notes manually.”</p>
                  <button data-testid="endscreen-enable-settings" onClick={() => { setShowEnd(false); onClose(); onOpenSettings?.(); }} className="press mt-2 h-9 px-4 rounded-full bg-white font-bold text-sm border-2 border-black flex items-center gap-2"><GearSix size={14} weight="bold" /> Enable in Settings</button>
                </div>
              )}

              <button data-testid="endscreen-skip-ai" onClick={() => setShowEnd(false)} className="press w-full h-11 rounded-xl border-2 border-black bg-white font-bold flex items-center gap-3 px-4"><Prohibit size={18} weight="bold" /> Skip AI Summary</button>
              <button data-testid="endscreen-remind-later" onClick={() => { setShowEnd(false); onClose(); }} className="press w-full h-11 rounded-xl border-2 border-black bg-white font-bold flex items-center gap-3 px-4"><BellRinging size={18} weight="bold" /> Remind me later</button>
            </div>
          </div>
        )}
      </motion.div>
    </div>
  );
}
